import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { apiClient } from "../services/apiClient";

const ChatContext = createContext();

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return context;
};

export const ChatProvider = ({ children }) => {
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [chatDocuments, setChatDocuments] = useState({});

  // Fetch all chat sessions
  const fetchSessions = useCallback(async (proposalId = null) => {
    try {
      setSessionsLoading(true);
      setError(null);

      const response = await apiClient.getChatSessions(proposalId);

      if (response.success) {
        // CRITICAL FIX: Ensure sessions have proper session_type
        const normalizedSessions = (response.data.sessions || []).map(
          (session) => {
            // Normalize session_type based on proposal_id
            let sessionType = session.session_type;

            // If session_type is missing, infer it from proposal_id
            if (!sessionType) {
              sessionType = session.proposal_id
                ? "proposal_specific"
                : "general";
            }

            // Ensure consistent property names
            return {
              ...session,
              session_type: sessionType,
              // Ensure we have session_id (handle both camelCase and snake_case from API)
              session_id: session.session_id || session.sessionId,
            };
          }
        );

        setSessions(normalizedSessions);
      } else {
        throw new Error(response.message || "Failed to fetch sessions");
      }
    } catch (error) {
      console.error("Error fetching sessions:", error);
      setError(error.message);
      setSessions([]);
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  // Refresh sessions method for immediate sidebar updates
  const refreshSessions = useCallback(async (proposalId = null) => {
    try {
      // Don't set loading state here to avoid UI flicker
      const response = await apiClient.getChatSessions(proposalId);

      if (response.success) {
        // CRITICAL FIX: Apply same normalization as fetchSessions
        const normalizedSessions = (response.data.sessions || []).map(
          (session) => {
            let sessionType = session.session_type;

            if (!sessionType) {
              sessionType = session.proposal_id
                ? "proposal_specific"
                : "general";
            }

            return {
              ...session,
              session_type: sessionType,
              session_id: session.session_id || session.sessionId,
            };
          }
        );

        setSessions(normalizedSessions);
      } else {
        console.error("Failed to refresh sessions:", response.message);
      }
    } catch (error) {
      console.error("Error refreshing sessions:", error);
    }
  }, []);

  // Fetch specific session with messages
  const fetchSession = useCallback(async (sessionId) => {
    try {
      setLoading(true);
      setError(null);

      // Clear current session first to trigger proper state reset
      setCurrentSession(null);
      setMessages([]);

      const response = await apiClient.getChatSession(sessionId);

      if (response.success) {
        const sessionData = response.data.session;

        // CRITICAL FIX: Normalize session data here too
        const normalizedSession = {
          ...sessionData,
          session_type:
            sessionData.session_type ||
            (sessionData.proposal_id ? "proposal_specific" : "general"),
          session_id: sessionData.session_id || sessionData.sessionId,
        };

        setCurrentSession(normalizedSession);
        setMessages(normalizedSession.messages || []);

        // Load chat documents for this session
        await fetchChatDocuments(sessionId);

        return normalizedSession;
      } else {
        throw new Error(response.message || "Failed to fetch session");
      }
    } catch (error) {
      console.error("Error fetching session:", error);
      setError(error.message);
      setCurrentSession(null);
      setMessages([]);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create new chat session
  const createSession = useCallback(async (sessionName, proposalId = null) => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.createChatSession(
        sessionName,
        proposalId
      );

      if (response.success) {
        const newSession = response.data.session;

        // CRITICAL FIX: Normalize new session data
        const normalizedNewSession = {
          ...newSession,
          session_type:
            newSession.session_type ||
            (proposalId ? "proposal_specific" : "general"),
          session_id: newSession.session_id || newSession.sessionId,
        };

        // Add to sessions list immediately (optimistic update)
        setSessions((prev) => [normalizedNewSession, ...prev]);

        return normalizedNewSession;
      } else {
        throw new Error(response.message || "Failed to create session");
      }
    } catch (error) {
      console.error("Error creating session:", error);

      // More detailed error logging
      if (error.response) {
        console.error("Response data:", error.response.data);
        console.error("Response status:", error.response.status);
      }

      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to create session";
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Upload documents to a chat session
  const uploadChatDocuments = useCallback(async (files, sessionId) => {
    try {
      setError(null);

      const formData = new FormData();
      files.forEach((file) => {
        formData.append("documents", file);
      });

      const response = await apiClient.post(
        `/api/chat/sessions/${sessionId}/documents`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.success) {
        // Refresh documents for this session
        await fetchChatDocuments(sessionId);
        return response.data;
      } else {
        throw new Error(response.message || "Failed to upload documents");
      }
    } catch (error) {
      console.error("Error uploading chat documents:", error);
      setError(error.message);
      throw error;
    }
  }, []);

  // Fetch documents for a chat session
  const fetchChatDocuments = useCallback(async (sessionId) => {
    try {
      const response = await apiClient.get(
        `/api/chat/sessions/${sessionId}/documents`
      );

      if (response.success) {
        setChatDocuments((prev) => ({
          ...prev,
          [sessionId]: response.data.documents,
        }));
        return response.data.documents;
      } else {
        throw new Error(response.message || "Failed to fetch chat documents");
      }
    } catch (error) {
      console.error("Error fetching chat documents:", error);
      setChatDocuments((prev) => ({
        ...prev,
        [sessionId]: [],
      }));
      return [];
    }
  }, []);

  // Remove a document from a chat session
  const removeChatDocument = useCallback(async (sessionId, documentId) => {
    try {
      setError(null);

      const response = await apiClient.delete(
        `/api/chat/sessions/${sessionId}/documents/${documentId}`
      );

      if (response.success) {
        // Remove from local state
        setChatDocuments((prev) => ({
          ...prev,
          [sessionId]: (prev[sessionId] || []).filter(
            (doc) => doc.chat_document_id !== documentId
          ),
        }));
        return true;
      } else {
        throw new Error(response.message || "Failed to remove document");
      }
    } catch (error) {
      console.error("Error removing chat document:", error);
      setError(error.message);
      throw error;
    }
  }, []);

  // Get documents for a specific session
  const getChatDocuments = useCallback(
    (sessionId) => {
      return chatDocuments[sessionId] || [];
    },
    [chatDocuments]
  );

  // Clear chat documents when session changes
  const clearChatDocuments = useCallback((sessionId) => {
    setChatDocuments((prev) => ({
      ...prev,
      [sessionId]: [],
    }));
  }, []);

  // Updated sendMessage function to handle session activation and refresh
  const sendMessage = async (sessionId, message, options = {}) => {
    try {
      setError(null);

      // Check if this is the first message to a new session
      const session = sessions.find((s) => s.session_id === sessionId);
      const isFirstMessage = !session || (session.message_count || 0) === 0;

      // Create and add user message immediately with referenced documents AND chat documents
      const userMessage = {
        message_id: `temp-user-${Date.now()}`, // Temporary ID
        message_type: "user",
        message_text: message,
        timestamp: new Date().toISOString(),
        session_id: sessionId,
        source_links: [], // This will be for AI responses
        ai_provider: null,
        model_used: null,
        processing_time_ms: null,
        // Add referenced documents for user messages
        referenced_documents: options.referencedDocuments || [],
        referenced_document_details: options.referencedDocumentDetails || [], // Full document objects
        // Add chat documents for user messages
        chat_documents: options.chatDocuments || [],
      };

      // Add user message to state immediately
      setMessages((prev) => [...prev, userMessage]);

      // Update session optimistically
      setSessions((prev) =>
        prev.map((session) =>
          session.session_id === sessionId
            ? {
                ...session,
                last_activity: new Date().toISOString(),
                message_count: (session.message_count || 0) + 1,
              }
            : session
        )
      );

      // Now set typing and make API call
      setIsTyping(true);

      const response = await apiClient.sendMessage(sessionId, message, {
        ...options,
        // Include referenced documents if provided
        referencedDocuments: options.referencedDocuments || [],
        // Include chat documents if provided
        chatDocuments: options.chatDocuments || [],
      });

      if (response.success) {
        // Create AI response message
        const aiMessage = {
          message_id: response.data.messageId || `ai-${Date.now()}`,
          message_type: "assistant",
          message_text: response.data.aiResponse,
          timestamp: new Date().toISOString(),
          session_id: sessionId,
          ai_provider: response.data.provider,
          model_used: response.data.model,
          processing_time_ms: response.data.processingTime,
          source_links: response.data.sources || [],
        };

        // Add AI response and update user message with real ID if provided
        setMessages((prev) => {
          const updatedMessages = [...prev];

          // If the API returns the actual user message data, update the temporary one
          if (response.data.userMessageId) {
            const tempUserIndex = updatedMessages.findIndex(
              (msg) => msg.message_id === userMessage.message_id
            );
            if (tempUserIndex !== -1) {
              updatedMessages[tempUserIndex] = {
                ...updatedMessages[tempUserIndex],
                message_id: response.data.userMessageId,
              };
            }
          }

          // Add AI response
          return [...updatedMessages, aiMessage];
        });

        // Update session with final message count
        setSessions((prev) =>
          prev.map((session) =>
            session.session_id === sessionId
              ? {
                  ...session,
                  last_activity: new Date().toISOString(),
                  message_count: (session.message_count || 0) + 1, // +1 more for AI response
                }
              : session
          )
        );

        // REFRESH SESSIONS if this was the first message (to show in sidebar)
        if (isFirstMessage) {
          await refreshSessions();
        }

        // Log context strategy for debugging
        if (response.data.contextStrategy) {
          console.log("AI Context Strategy:", response.data.contextStrategy);
          console.log("Documents Used:", response.data.documentsUsed);
        }

        return aiMessage;
      } else {
        throw new Error(response.message || "Failed to send message");
      }
    } catch (error) {
      console.error("ChatContext: Send message error:", error);
      console.error("ChatContext: Error response data:", error.response?.data);
      console.error("ChatContext: Error status:", error.response?.status);
      console.error("ChatContext: Error config:", error.config);

      // Remove the temporary user message on error
      setMessages((prev) =>
        prev.filter((msg) => msg.message_id !== `temp-user-${Date.now()}`)
      );

      // Revert session update on error
      setSessions((prev) =>
        prev.map((session) =>
          session.session_id === sessionId
            ? {
                ...session,
                message_count: Math.max((session.message_count || 0) - 1, 0),
              }
            : session
        )
      );

      setError(error.response?.data?.message || error.message);
      throw error;
    } finally {
      setIsTyping(false);
    }
  };

  // Update session (rename, pin/unpin)
  const updateSession = useCallback(
    async (sessionId, updates) => {
      try {
        setError(null);

        const response = await apiClient.updateChatSession(sessionId, updates);

        if (response.success) {
          // Update in sessions list
          setSessions((prev) =>
            prev.map((session) =>
              session.session_id === sessionId
                ? { ...session, ...updates }
                : session
            )
          );

          // Update current session if it's the same
          if (currentSession && currentSession.session_id === sessionId) {
            setCurrentSession((prev) => ({ ...prev, ...updates }));
          }

          return true;
        } else {
          throw new Error(response.message || "Failed to update session");
        }
      } catch (error) {
        console.error("Error updating session:", error);
        setError(error.message);
        return false;
      }
    },
    [currentSession]
  );

  // Delete session
  const deleteSession = useCallback(
    async (sessionId) => {
      try {
        setError(null);

        const response = await apiClient.deleteChatSession(sessionId);

        if (response.success) {
          // Remove from sessions list
          setSessions((prev) =>
            prev.filter((session) => session.session_id !== sessionId)
          );

          // Clear current session if it's the deleted one
          if (currentSession && currentSession.session_id === sessionId) {
            setCurrentSession(null);
            setMessages([]);
          }

          // Clear chat documents for this session
          setChatDocuments((prev) => {
            const updated = { ...prev };
            delete updated[sessionId];
            return updated;
          });

          return true;
        } else {
          throw new Error(response.message || "Failed to delete session");
        }
      } catch (error) {
        console.error("Error deleting session:", error);
        setError(error.message);
        return false;
      }
    },
    [currentSession]
  );

  // Search messages
  const searchMessages = useCallback(async (query, proposalId = null) => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.searchMessages(query, proposalId);

      if (response.success) {
        return response.data.results || [];
      } else {
        throw new Error(response.message || "Search failed");
      }
    } catch (error) {
      console.error("Error searching messages:", error);
      setError(error.message);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Clear current session
  const clearCurrentSession = useCallback(() => {
    setCurrentSession(null);
    setMessages([]);
    setError(null);
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Get sessions by type with proper filtering
  const getGeneralSessions = useCallback(() => {
    const generalSessions = sessions.filter((session) => {
      const isGeneral = session.session_type === "general";
      return isGeneral;
    });

    return generalSessions;
  }, [sessions]);

  const getProposalSessions = useCallback(
    (proposalId = null) => {
      const proposalSessions = sessions.filter((session) => {
        const isProposalSpecific = session.session_type === "proposal_specific";
        const matchesProposal = proposalId
          ? session.proposal_id === proposalId
          : true;

        return isProposalSpecific && matchesProposal;
      });

      return proposalSessions;
    },
    [sessions]
  );

  // Initialize sessions on mount
  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  // Clear current session when navigating away from proposal workspace
  useEffect(() => {
    const handleNavigationCleanup = () => {
      // This will be called when the component unmounts or when we want to clean up
      if (
        currentSession &&
        currentSession.session_type === "proposal_specific"
      ) {
        clearCurrentSession();
      }
    };

    // Listen for navigation events (you might want to trigger this from your navigation logic)
    window.addEventListener("clearProposalSession", handleNavigationCleanup);

    return () => {
      window.removeEventListener(
        "clearProposalSession",
        handleNavigationCleanup
      );
    };
  }, [currentSession, clearCurrentSession]);

  const value = {
    // State
    sessions,
    currentSession,
    messages,
    setMessages, // Make setMessages available to components
    loading,
    sessionsLoading,
    error,
    isTyping,

    // Actions
    fetchSessions,
    fetchSession,
    createSession,
    sendMessage,
    updateSession,
    deleteSession,
    searchMessages,
    clearCurrentSession,
    clearError,
    refreshSessions,

    // Chat document methods
    chatDocuments,
    uploadChatDocuments,
    fetchChatDocuments,
    removeChatDocument,
    getChatDocuments,
    clearChatDocuments,

    // Computed
    getGeneralSessions,
    getProposalSessions,

    // Utils
    hasSession: !!currentSession,
    messageCount: messages.length,
    sessionCount: sessions.length,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export default ChatContext;
